// MT5Tab.jsx — MT5 Terminal Dashboard
// Connected to Hostinger VPS MT5 via Kara Scanner /api/mt5-mcp/* routes

import React, { useState, useEffect, useCallback, useRef } from "react";
import Mt5EaLab from "./Mt5EaLab";
import { selectMt5AccountView, normalizeMt5Bars, mt5BarFreshness } from "./mt5AccountView";

const C = {
  bg: "#070d1a", panel: "#0d1f35", panel2: "#112744", border: "#1e3a5f",
  text: "#f0f6ff", muted: "#96adc8", dim: "#4a6480",
  green: "#00d4aa", amber: "#f59e0b", red: "#ef4444",
  purple: "#a855f7", blue: "#38bdf8", cyan: "#06b6d4", orange: "#f97316",
};

const apiFetch = (url, opts = {}) => {
  const token = localStorage.getItem("kara_auth_token");
  return fetch(url, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": "Bearer " + token } : {}),
      ...(opts.headers || {}),
    },
  });
};

// ── Small UI atoms ────────────────────────────────────────────────────────────

const Badge = ({ label, color }) => (
  <span style={{
    background: `${color}22`, color, border: `1px solid ${color}44`,
    borderRadius: 3, padding: "3px 9px", fontSize: 10, fontWeight: 700,
    display: "inline-block", letterSpacing: 0.5
  }}>{label}</span>
);

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", flex: 1, minWidth: 80 }}>
      <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: color || C.text, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const SectionCard = ({ title, children, badge, action, actionLabel, actionLoading }) => (
  <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 14px", borderBottom: `1px solid ${C.border}`,
      background: C.bg
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.text, letterSpacing: 1 }}>{title}</span>
        {badge}
      </div>
      {action && (
        <button onClick={action} disabled={actionLoading}
          style={{
            padding: "5px 12px", fontSize: 11, fontWeight: 700,
            background: actionLoading ? C.dim : `${C.blue}22`,
            color: actionLoading ? C.muted : C.blue,
            border: `1px solid ${actionLoading ? C.dim : C.blue}55`,
            borderRadius: 4, cursor: actionLoading ? "not-allowed" : "pointer",
          }}>
          {actionLoading ? "⟳" : actionLabel}
        </button>
      )}
    </div>
    <div style={{ padding: "12px 14px" }}>{children}</div>
  </div>
);

// ── Forward test card: per-EA breakdown on the demo account via kara-mt5-forward ──
// 2026-10-12 hermes: rewritten to surface multi-EA runner (EA5 + EA10) with
// prominent live balance/equity, today's trades, and per-EA stats.
const FWD_STATUS = {
  weekend_halt: ["WEEKEND HALT", C.amber],
  running: ["RUNNING", C.green],
  checking: ["CHECKING", C.blue],
  degraded: ["DEGRADED", C.red],
  market_stale: ["MARKET DATA STALE", C.red],
  disconnected: ["DISCONNECTED", C.red],
  unverified: ["UNVERIFIED", C.red],
  algo_trading_off: ["ALGO TRADING OFF", C.amber],
  paused: ["PAUSED", C.amber],
  daily_loss_limit: ["DAILY LIMIT", C.amber],
  daily_profit_target_reached: ["DAILY PROFIT LOCK", C.amber],
  daily_drawdown_limit_reached: ["DAILY DRAWDOWN LOCK", C.red],
  disabled: ["DISABLED", C.dim],
  error: ["ERROR", C.red],
  blocked_not_demo: ["BLOCKED — NOT DEMO", C.red],
  blocked_wrong_account: ["BLOCKED — WRONG ACCOUNT", C.red],
  not_started: ["NOT STARTED", C.dim],
};
const GATE_LABELS = { min_trades: "Trades", win_rate: "Win rate %", profit_factor: "Profit factor", expectancy_r: "Expectancy R", max_drawdown: "Max DD % (≤)" };
// EA names and operational metadata come from the live status payload; colors are presentation only.
// 2026-10-29 claude (Kenyon: unique EA names, keep history): display_name from multi_ea_config.json; the ID (trades.strategy) never changes.
const eaMeta = (id, cfgEas) => { const n = cfgEas?.[id]?.display_name; return { name: n || id || "—", short: n || id || "—", id: id || "—", color: C.cyan }; };
const eaStateColor = (state) => state === "running" ? C.green
  : state === "checking" ? C.blue
  : ["weekend_halt", "daily_profit_target_reached", "algo_trading_off", "manual"].includes(state) ? C.amber
  : state === "disabled" ? C.dim
  : ["error", "degraded", "market_stale", "unverified", "disconnected", "blocked_not_demo", "blocked_wrong_account", "daily_drawdown_limit_reached", "killed"].includes(state) ? C.red
  : C.muted;
function fmtAgo(ms) { if (!ms) return "—"; const m = Math.round((Date.now() - ms) / 60000); return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}m ago` : `${Math.floor(m / 1440)}d ago`; }

function fmtMoney(n, currency) { return n == null || !currency ? "—" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(2)} ${currency}`; }
function fmtUsd(n) { if (n == null) return "—"; const s = n >= 0 ? "+" : ""; return `${s}$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtR(n) { if (n == null) return "—"; const s = n >= 0 ? "+" : ""; return `${s}${n}R`; }
function fmtTs(ms) { if (!ms) return "—"; const d = new Date(ms); return d.toISOString().slice(11, 16) + "Z"; }

function ForwardTestCard() {
  const [fwd, setFwd] = useState(null);
  const [acct, setAcct] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const [accountConfirmedAt, setAccountConfirmedAt] = useState(null);
  const [accountPending, setAccountPending] = useState(false);
  const accountRequestRef = useRef(0);
  const [live, setLive] = useState(null);
  const load = useCallback(() => {
    apiFetch("/api/mt5-forward/status").then(r => r.json()).then(setFwd).catch(e => setFwd({ ok: false, error: e.message }));

    const requestId = ++accountRequestRef.current;
    setAccountPending(true);
    apiFetch("/api/mt5-mcp/account")
      .then(async (r) => {
        let d = {};
        try { d = await r.json(); } catch {}
        if (requestId !== accountRequestRef.current) return;
        if (r.ok && d.ok && d.data?.login != null) {
          setAcct(d.data);
          setAccountConfirmedAt(Number(d.as_of) || Date.now());
          setAccountError(null);
          return;
        }
        setAccountError({
          code: d.code || (r.status === 401 ? "auth_required" : "account_unavailable"),
          message: d.error || "MT5 account details are temporarily unavailable",
        });
      })
      .catch(() => {
        if (requestId !== accountRequestRef.current) return;
        setAccountError({ code: "network_error", message: "MT5 account service is temporarily unavailable" });
      })
      .finally(() => {
        if (requestId === accountRequestRef.current) setAccountPending(false);
      });
  }, []);
  // live positions (broker P&L) — heavier (3 MCP calls), so every 15s
  const loadLive = useCallback(() => {
    apiFetch("/api/mt5-chat/summary").then(r => r.json()).then(setLive).catch(e => setLive({ ok: false, error: e.message }));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);
  useEffect(() => { loadLive(); const id = setInterval(loadLive, 15000); return () => clearInterval(id); }, [loadLive]);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") { load(); loadLive(); }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, [load, loadLive]);
  const livePositions = live?.ok && Array.isArray(live?.open_positions) ? live.open_positions : null;
  const openPnl = livePositions ? livePositions.reduce((s, p) => s + (Number(p.profit) || 0), 0) : null;
  const last24 = fwd?.last_24h || {};
  const last24Target = Number.isFinite(Number(last24.target)) ? Number(last24.target) : null;
  const last24TargetMet = last24Target != null && Number(last24.ea_opened || 0) >= last24Target;
  const recent = (fwd?.trades || []).slice(0, 15);
  const events = (fwd?.events || []).filter(e => !/tick crashed: unknown EA/.test(e.msg)).slice(0, 12);

  const st = fwd?.stats || {};
  const runnerStale = !fwd?.runner_heartbeat || Date.now() - fwd.runner_heartbeat > 5 * 60 * 1000;
  const cycleStale = !fwd?.last_successful_cycle_at || Date.now() - fwd.last_successful_cycle_at > 5 * 60 * 1000;
  const [label, color] = !fwd?.ok ? ["LOADING", C.muted]
    : runnerStale || fwd?.health?.runner_alive !== true ? ["RUNNER STALE", C.red]
      : fwd?.health?.worker_reachable !== true ? ["WORKER UNVERIFIED", C.red]
        : cycleStale || fwd?.health?.cycle_fresh !== true ? ["NO FRESH CYCLE", C.red]
          : (FWD_STATUS[fwd?.status] || [String(fwd?.status || "…").toUpperCase(), C.muted]);
  const eas = fwd?.eas || [];
  const tradesToday = fwd?.trades_today || [];
  const openTrades = tradesToday.filter(t => t.status === "open");
  const closedToday = tradesToday.filter(t => t.status === "closed");
  const todayPnl = fwd?.today?.ea?.pnl_account;
  const todayLosses = closedToday.filter(t => (t.pnl_account || 0) < 0).length;
  const todayWins = closedToday.filter(t => (t.pnl_account || 0) > 0).length;

  const accountView = selectMt5AccountView({
    liveAccount: acct,
    liveError: accountError,
    liveConfirmedAt: accountConfirmedAt,
    forwardStatus: fwd,
  });
  const displayAcct = accountView.account;
  const liveBal = displayAcct?.balance;
  const liveEquity = displayAcct?.equity;
  const liveMargin = displayAcct?.margin || 0;
  const liveCurrency = displayAcct?.currency || fwd?.account_currency || "—";
  const accountAgeMinutes = accountView.ageMs == null ? null : Math.max(1, Math.ceil(accountView.ageMs / 60000));
  const accountModeLabel = accountView.mode === "live" ? (accountPending ? "REFRESHING" : "LIVE")
    : accountError?.code === "worker_down" && !accountView.blocked ? "VPS WORKER DOWN"
    : accountView.mode === "reconnecting" ? "RECONNECTING"
      : accountView.mode === "wrong_account" ? "WRONG ACCOUNT BLOCKED"
        : accountView.mode === "not_demo" ? "NON-DEMO BLOCKED"
          : accountView.mode === "stale_expired" ? "SNAPSHOT EXPIRED"
            : "WAITING";
  const accountModeColor = accountView.mode === "live" ? C.green
    : accountView.mode === "reconnecting" ? C.amber
      : accountView.blocked || accountView.mode === "stale_expired" ? C.red
        : C.muted;
  const accountNotice = accountError?.code === "worker_down" && !accountView.blocked
    ? `⛔ ${accountError.message}${accountView.confirmedAt ? ` — last confirmed ${fmtTs(accountView.confirmedAt)}` : ""}`
    : accountView.mode === "reconnecting"
    ? `⚠ MT5 reconnecting — last confirmed ${fmtTs(accountView.confirmedAt)} (${accountAgeMinutes}m ago)`
    : accountView.mode === "wrong_account"
      ? "⛔ Trading blocked: MT5 is connected to an unexpected account. Cached balances are hidden."
      : accountView.mode === "not_demo"
        ? "⛔ Trading blocked: the terminal is not on the approved demo account. Cached balances are hidden."
        : accountView.mode === "stale_expired"
          ? "⚠ The last confirmed account snapshot is older than 30 minutes and has been hidden."
          : accountView.mode === "waiting_for_first_tick"
            ? (accountError?.message || "Waiting for the first confirmed MT5 account snapshot…")
            : null;

  return (
    <SectionCard title={`MT5 EA TRADING — ${fwd?.enabled_count ?? "—"}/${fwd?.configured_count ?? "—"} ENABLED EAs × ${fwd?.symbol_count ?? "—"} SYMBOLS (DEMO)`}
      badge={<Badge label={label} color={color} />} action={() => { load(); loadLive(); }} actionLabel="⟳">
      {!fwd?.ok ? (
        <div style={{ color: C.muted, fontSize: 11 }}>{fwd?.error ? `Error: ${fwd.error}` : "Loading…"}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* ── LIVE OR RECENTLY CONFIRMED MT5 DEMO ACCOUNT ── */}
          <div style={{
            background: `linear-gradient(135deg, ${C.panel2} 0%, ${C.bg} 100%)`,
            border: `1px solid ${accountModeColor}88`, borderRadius: 8, padding: "12px 14px",
            display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
          }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, letterSpacing: 1 }}>
                  MT5 ACCOUNT — {displayAcct?.name || "—"} · LOGIN {displayAcct?.login || "—"} · {displayAcct?.server || "—"}
                </div>
                <Badge label={accountModeLabel} color={accountModeColor} />
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5 }}>BALANCE</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: C.cyan, fontFamily: "monospace" }}>
                    {liveBal != null ? `${liveBal.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${liveCurrency}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5 }}>EQUITY</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: liveEquity >= liveBal ? C.green : C.red, fontFamily: "monospace" }}>
                    {liveEquity != null ? `${liveEquity.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${liveCurrency}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5 }}>MARGIN</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: liveMargin > 0 ? C.amber : C.muted, fontFamily: "monospace" }}>
                    {liveBal != null ? `${liveMargin.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${liveCurrency}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5 }}>LEVERAGE</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: "monospace" }}>
                    {displayAcct?.leverage ? `1:${displayAcct.leverage}` : "—"}
                  </div>
                </div>
              </div>
              {accountNotice ? <div style={{ fontSize: 10, color: accountModeColor, marginTop: 6, fontWeight: 700 }}>{accountNotice}</div> : null}
              {fwd.paused_reason && fwd.status !== "weekend_halt" && !accountView.blocked ? <div style={{ fontSize: 10, color: C.amber, marginTop: 6 }}>⏸ {fwd.paused_reason}</div> : null}
              {fwd.weekend_reason ? <div style={{ color: C.amber, fontSize: 10 }}>⏸ {fwd.weekend_reason}</div> : null}
              {fwd.last_error && accountView.mode === "live" ? <div style={{ fontSize: 10, color: C.red, marginTop: 6 }}>⚠ The EA runner reported an error and is retrying automatically.</div> : null}
            </div>
          </div>

          {/* Runner liveness, worker reachability, market data and lane execution are separate facts. */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <StatCard label="RUNNER HEARTBEAT"
              value={fwd.health?.runner_alive === true ? "ALIVE" : "UNVERIFIED"}
              sub={fwd.runner_heartbeat ? fmtAgo(fwd.runner_heartbeat) : "no heartbeat recorded"}
              color={fwd.health?.runner_alive === true ? C.green : C.red} />
            <StatCard label="MT5 WORKER"
              value={fwd.health?.worker_reachable === true ? "REACHABLE" : fwd.health?.worker_reachable === false ? "UNREACHABLE" : "UNVERIFIED"}
              sub={fwd.health?.checked_at ? `checked ${fmtAgo(fwd.health.checked_at)}` : "no worker evidence"}
              color={fwd.health?.worker_reachable === true ? C.green : C.red} />
            <StatCard label="COMPLETED EA CYCLE"
              value={fwd.health?.cycle_fresh === true ? "FRESH" : "STALE / MISSING"}
              sub={fwd.last_successful_cycle_at ? fmtAgo(fwd.last_successful_cycle_at) : "no completed cycle"}
              color={fwd.health?.cycle_fresh === true ? C.green : C.red} />
            <StatCard label="MARKET DATA"
              value={(fwd.market?.state || "unverified").replaceAll("_", " ").toUpperCase()}
              sub={fwd.market?.reason || (fwd.market?.checked_at != null && fwd.market?.tick_age_sec != null ? `tick age ${fwd.market.tick_age_sec}s · checked ${fmtAgo(fwd.market.checked_at)}` : "no market-data detail")}
              color={fwd.market?.state === "open" ? C.green : fwd.market?.state === "weekend_halt" ? C.amber : C.red} />
            <StatCard label="EA LANES"
              value={fwd.cycle_summary ? `${fwd.cycle_summary.reporting_lanes}/${fwd.cycle_summary.expected_lanes}` : "—"}
              sub={fwd.cycle_summary ? `${fwd.cycle_summary.fault_lanes} faulted · source: runner telemetry` : "no lane telemetry"}
              color={fwd.cycle_summary ? (fwd.cycle_summary.fault_lanes === 0 ? C.green : C.red) : C.muted} />
          </div>

          {/* ── TODAY'S SUMMARY ── */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <StatCard label="EA TRADES · LAST 24H" value={last24Target == null ? (last24.ea_opened ?? "—") : `${last24.ea_opened ?? "—"} / ${last24Target}`}
              sub={`${last24Target == null ? "no configured daily target" : `target ≥${last24Target}/day`} · last tick ${fmtAgo(fwd.last_tick)}${last24.ea_failed ? ` · ${last24.ea_failed} failed` : ""}`}
              color={last24Target == null ? C.text : last24TargetMet ? C.green : C.amber} />
            <StatCard label="OPEN NOW · LIVE P&L"
              value={livePositions ? `${livePositions.length} · ${openPnl >= 0 ? "+" : ""}${openPnl.toFixed(2)}` : "—"}
              sub={livePositions ? `${liveCurrency}, live from the broker` : "live positions unavailable — not inferred from cached data"}
              color={livePositions ? (openPnl >= 0 ? C.green : C.red) : C.muted} />
            <StatCard label="ALL TRADES TODAY" value={tradesToday.length} sub={`${todayWins}W / ${todayLosses}L · ${openTrades.length} open · ${tradesToday.filter(t => t.status === "failed").length} failed`} color={C.text} />
            <StatCard label="EA SETTLED P&L TODAY (UTC)" value={fmtMoney(todayPnl, fwd.account_currency)} sub={`Chat: ${fmtMoney(fwd.today?.chat?.pnl_account, fwd.account_currency)}`} color={todayPnl == null ? C.muted : todayPnl >= 0 ? C.green : C.red} />
            <StatCard label="SETTLED P&L · USD CONVERSION" value={fmtUsd(st.pnl_usd)} sub={`${fmtR(st.total_r)} · max DD ${st.max_drawdown_pct ?? "—"}%`} color={st.pnl_usd == null ? C.muted : st.pnl_usd >= 0 ? C.green : C.red} />
            <StatCard label="OVERALL WR" value={st.win_rate_pct != null ? `${st.win_rate_pct}%` : "—"} sub={`${st.wins}W / ${st.losses}L · ${st.trades} trades`} color={(st.win_rate_pct ?? 0) >= 40 ? C.green : C.amber} />
          </div>

          {/* ── PER-EA BREAKDOWN (the part you actually want to see) ── */}
          {eas.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1 }}>PER-EA BREAKDOWN</div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", fontFamily: "monospace" }}>
                <thead>
                  <tr style={{ color: C.muted, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: "6px 4px" }}>EA</th>
                    <th style={{ padding: "6px 4px" }}>Symbol · TF</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>24h</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Today</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>P&L Today</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Trades</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>WR</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Total R</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Total P&L</th>
                    <th style={{ padding: "6px 4px", textAlign: "right" }}>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {eas.map(ea => {
                    const meta = eaMeta(ea.id, fwd?.config?.eas);
                    const eaToday = tradesToday.filter(t => t.strategy === ea.id);
                    const eaTodayPnl = ea.today?.pnl_account;
                    return (
                      <tr key={ea.id} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: "6px 4px", color: meta.color, fontWeight: 700 }} title={[ea.runtime?.reason, ea.module].filter(Boolean).join(" · ")}>
                          {meta.name}{meta.name !== meta.id ? <span style={{ fontSize: 9, fontWeight: 400, color: C.dim }}> ({meta.id})</span> : null} <Badge label={(ea.runtime?.state || "unknown").replaceAll("_", " ").toUpperCase()} color={eaStateColor(ea.runtime?.state)} />
                          {ea.runtime?.reason ? <div style={{ fontSize: 9, fontWeight: 400, color: eaStateColor(ea.runtime?.state) }}>{ea.runtime.reason}</div> : null}
                        </td>
                        <td style={{ padding: "6px 4px", color: C.muted }} title={ea.lane_summary ? Object.entries(ea.lane_summary.states || {}).map(([state, n]) => `${n} ${state.replaceAll("_", " ")}`).join(" · ") : undefined}>
                          {ea.manual ? "manual broker trade" : `${fwd.symbol_count ?? "—"} sym · ${ea.timeframe || "TF unavailable"}${ea.track ? ` · ${ea.track}` : ""}${ea.risk_pct != null ? ` · risk ${ea.risk_pct}%` : ""}`}
                          {ea.lane_summary ? <div style={{ fontSize: 9, color: ea.lane_summary.faults ? C.red : C.green }}>
                            {ea.lane_summary.reporting}/{ea.lane_summary.expected} lanes reporting · {ea.lane_summary.faults} faulted · updated {fmtAgo(ea.lane_summary.last_checked_at)}
                          </div> : null}
                          {ea.killed_lanes?.length ? <span style={{ color: C.red }} title={`lanes auto-killed at ≤${fwd.config?.kill_switch?.min_total_r ?? "—"}R over last ${fwd.config?.kill_switch?.window ?? "—"} trades`}> · ✖ {ea.killed_lanes.join(",")}</span> : null}
                        </td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{ea.opened_24h ?? "—"}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{eaToday.length}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: eaTodayPnl == null ? C.muted : eaTodayPnl >= 0 ? C.green : C.red, fontWeight: 700 }}>{fmtMoney(eaTodayPnl, fwd.account_currency)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>{ea.stats.trades}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: (ea.stats.win_rate_pct ?? 0) >= 40 ? C.green : C.amber }}>
                          {ea.stats.win_rate_pct != null ? `${ea.stats.win_rate_pct}%` : "—"}
                        </td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: (ea.stats.total_r ?? 0) >= 0 ? C.green : C.red }}>{fmtR(ea.stats.total_r)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: (ea.stats.pnl_account ?? 0) >= 0 ? C.green : C.red, fontWeight: 700 }}>{fmtMoney(ea.stats.pnl_account, fwd.account_currency)}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", color: ea.open ? C.amber : C.dim }}>{livePositions ? livePositions.filter(p => p.owner === ea.id).length : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── OPEN POSITIONS — live from the broker ── */}
          <div style={{ background: C.bg, border: `1px solid ${C.amber}55`, borderRadius: 6, padding: "8px 10px", overflowX: "auto" }}>
            <div style={{ fontSize: 10, color: C.amber, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>⏵ OPEN POSITIONS — LIVE ({livePositions ? livePositions.length : "—"}) · {liveCurrency}</div>
            {!livePositions ? <div style={{ fontSize: 11, color: C.muted }}>Live positions unavailable. Cached account balance is not being used to infer P&amp;L.</div> : livePositions.length === 0 ? <div style={{ fontSize: 11, color: C.muted }}>No open MT5 positions.</div> : (
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", fontFamily: "monospace" }}>
                <tbody>
                  {livePositions.map(p => {
                    const m = eaMeta(p.owner, fwd?.config?.eas);
                    return (
                      <tr key={p.ticket} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: "3px 4px", color: m.color, fontWeight: 700 }}>{m.short}</td>
                        <td style={{ padding: "3px 4px", color: C.muted }}>#{p.ticket}</td>
                        <td style={{ padding: "3px 4px", color: p.side === "buy" ? C.green : C.red, fontWeight: 700 }}>{String(p.side).toUpperCase()}</td>
                        <td style={{ padding: "3px 4px" }}>{p.volume} {p.symbol}</td>
                        <td style={{ padding: "3px 4px", color: C.muted }}>@ {p.entry} · SL {p.sl} · TP {p.tp}</td>
                        <td style={{ padding: "3px 4px", textAlign: "right", color: (p.profit || 0) >= 0 ? C.green : C.red, fontWeight: 700 }}>{(p.profit || 0) >= 0 ? "+" : ""}{Number(p.profit || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── TODAY'S TRADE LOG (chronological) ── */}
          {tradesToday.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>TRADE LOG — TODAY (UTC {new Date().toISOString().slice(0, 10)})</div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", fontFamily: "monospace" }}>
                <thead>
                  <tr style={{ color: C.muted, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: "4px 4px" }}>Time</th>
                    <th style={{ padding: "4px 4px" }}>EA</th>
                    <th style={{ padding: "4px 4px" }}>Symbol · TF</th>
                    <th style={{ padding: "4px 4px" }}>Side</th>
                    <th style={{ padding: "4px 4px", textAlign: "right" }}>Vol</th>
                    <th style={{ padding: "4px 4px", textAlign: "right" }}>Entry</th>
                    <th style={{ padding: "4px 4px" }}>SL / TP</th>
                    <th style={{ padding: "4px 4px" }}>Status</th>
                    <th style={{ padding: "4px 4px", textAlign: "right" }}>R</th>
                    <th style={{ padding: "4px 4px", textAlign: "right" }}>P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {tradesToday.map(t => {
                    const meta = eaMeta(t.strategy, fwd?.config?.eas);
                    const eaTag = meta.short;
                    const isOpen = t.status === "open";
                    return (
                      <tr key={t.id} style={{ borderTop: `1px solid ${C.border}`, background: isOpen ? `${C.amber}11` : "transparent" }}>
                        <td style={{ padding: "4px", color: C.muted }}>{fmtTs(t.opened_at)}</td>
                        <td style={{ padding: "4px", color: meta.color, fontWeight: 700 }}>{eaTag}</td>
                        <td style={{ padding: "4px", color: C.muted }}>{t.symbol} · {t.timeframe || "—"}</td>
                        <td style={{ padding: "4px", color: t.side === "buy" ? C.green : C.red, fontWeight: 700 }}>{String(t.side ?? "").toUpperCase()}</td>
                        <td style={{ padding: "4px", textAlign: "right" }}>{t.volume}</td>
                        <td style={{ padding: "4px", textAlign: "right" }}>{t.entry_price}</td>
                        <td style={{ padding: "4px", color: C.muted }}>{t.sl} / {t.tp}</td>
                        <td style={{ padding: "4px", color: isOpen ? C.amber : (t.pnl_account || 0) >= 0 ? C.green : C.red, fontWeight: 700 }}>
                          {isOpen ? "OPEN" : (t.exit_reason || t.status)}
                        </td>
                        <td style={{ padding: "4px", textAlign: "right", color: (t.r_multiple ?? 0) >= 0 ? C.green : C.red }}>{fmtR(t.r_multiple)}</td>
                        <td style={{ padding: "4px", textAlign: "right", color: (t.pnl_account ?? 0) >= 0 ? C.green : C.red, fontWeight: 700 }}>{fmtMoney(t.pnl_account, fwd.account_currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>NO TRADES YET TODAY (UTC) — MOST RECENT TRADES</div>
              {recent.length === 0 ? <div style={{ fontSize: 11, color: C.muted }}>No trades recorded yet.</div> : recent.map(t => {
                const m = eaMeta(t.strategy, fwd?.config?.eas);
                return (
                  <div key={t.id} style={{ fontSize: 11, fontFamily: "monospace", padding: "2px 0", color: C.text }}>
                    <span style={{ color: C.muted }}>{new Date(t.opened_at).toISOString().slice(5, 16).replace("T", " ")}Z</span>{" "}
                    <span style={{ color: m.color, fontWeight: 700 }}>{m.short}</span> {String(t.side).toUpperCase()} {t.symbol} ·{" "}
                    <span style={{ color: t.status === "open" ? C.amber : (t.pnl_account || 0) >= 0 ? C.green : C.red }}>{t.status === "open" ? "OPEN" : `${t.exit_reason || t.status} ${fmtMoney(t.pnl_account, fwd.account_currency)}`}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── RUNNER ACTIVITY — what the EA runner did recently ── */}
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px" }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>RUNNER ACTIVITY</div>
            {events.length === 0 ? <div style={{ fontSize: 11, color: C.muted }}>No runner events yet.</div> : events.map((e, i) => (
              <div key={i} style={{ fontSize: 10.5, fontFamily: "monospace", padding: "1px 0", color: e.level === "error" ? C.red : e.level === "warn" ? C.amber : C.text, overflowWrap: "anywhere" }}>
                <span style={{ color: C.muted }}>{fmtTs(e.ts)}</span> {e.msg}
              </div>
            ))}
          </div>

          {/* ── PROMOTION GATES ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
            {Object.entries(st.gates || {}).map(([k, g]) => (
              <div key={k} style={{ background: C.bg, border: `1px solid ${g.pass ? C.green : C.border}55`, borderRadius: 6, padding: "6px 8px", fontSize: 11 }}>
                <div style={{ color: C.muted }}>{GATE_LABELS[k] || k}</div>
                <div style={{ color: g.pass ? C.green : C.text, fontFamily: "monospace" }}>{g.pass ? "✓" : "○"} {g.have ?? "—"} / {g.need}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: st.promotable ? C.green : C.muted }}>
            {st.promotable ? "✅ All gates passed — ready for review / FundedNext port (needs Kenyon approval)." : "Promotion needs every gate above, from live demo trades only."}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Live MCP Activity — what the MT5 MCP server is doing RIGHT NOW
// Polls /api/mt5-mcp/activity every 3s; shows last 20 calls + "latest"
// ══════════════════════════════════════════════════════════════════════════
function McpActivityCard() {
  const [act, setAct] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    apiFetch("/api/mt5-mcp/activity?limit=20")
      .then(r => r.json())
      .then(d => setAct(d))
      .catch(e => setAct({ ok: false, error: e.message }));
    setTick(t => t + 1);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  const entries = act?.data?.entries || [];
  const serverTime = entries.reduce((latest, e) => { const t = Date.parse(e.ts); return Number.isFinite(t) ? Math.max(latest, t) : latest; }, 0);
  const ageMs = serverTime ? Date.now() - new Date(serverTime).getTime() : null;
  const live = ageMs != null && ageMs < 10000;
  const methods = {};
  for (const e of entries) methods[e.method] = (methods[e.method] || 0) + 1;
  const last = entries[0];

  return (
    <SectionCard
      title="MCP ACTIVITY — LIVE STREAM"
      badge={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          {live && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, boxShadow: `0 0 6px ${C.green}`, animation: "mt5-pulse 1.4s infinite" }} />}
          <Badge label={live ? "LIVE" : "STALE"} color={live ? C.green : C.amber} />
        </span>
      }
      action={load}
      actionLabel="⟳"
    >
      <style>{`@keyframes mt5-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }`}</style>
      {!act?.ok ? (
        <div style={{ color: C.muted, fontSize: 11 }}>{act?.error ? `Error: ${act.error}` : "Loading…"}</div>
      ) : entries.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 11 }}>No activity recorded yet — fire any /api/mt5-mcp/* call to see the stream.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {last && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <StatCard label="LAST CALL" value={last.method} sub={last.params?.length ? `args: ${last.params.join(",")}` : "no args"} color={C.cyan} />
              <StatCard label="LATENCY" value={`${last.latency_ms}ms`} sub={`HTTP ${last.status || "—"}`} color={last.latency_ms < 200 ? C.green : last.latency_ms < 1000 ? C.amber : C.red} />
              <StatCard label="LAST 20" value={entries.length} sub={`${Object.keys(methods).length} distinct tools`} color={C.text} />
              <StatCard label="ERRORS" value={entries.filter(e => !e.ok).length} sub={`in last ${entries.length} calls`} color={entries.some(e => !e.ok) ? C.red : C.green} />
            </div>
          )}
          <div style={{ maxHeight: 200, overflowY: "auto", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6 }}>
            <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse", fontFamily: "monospace" }}>
              <thead><tr style={{ color: C.muted, position: "sticky", top: 0, background: C.bg, zIndex: 1, textAlign: "left" }}>
                <th style={{ padding: "5px 8px" }}>Time</th>
                <th>Tool</th>
                <th>Args</th>
                <th style={{ textAlign: "right" }}>Status</th>
                <th style={{ textAlign: "right" }}>ms</th>
                <th>From</th>
              </tr></thead>
              <tbody>
                {entries.map((e, i) => {
                  const t = e.ts ? new Date(e.ts) : null;
                  const ago = t ? Math.max(0, Math.round((Date.now() - t.getTime()) / 1000)) : null;
                  const color = e.ok ? C.green : C.red;
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}33` }}>
                      <td style={{ padding: "4px 8px", color: ago != null && ago < 5 ? C.green : C.muted }}>{ago != null ? `${ago}s` : "—"}</td>
                      <td style={{ color: C.text }}>{e.method}</td>
                      <td style={{ color: C.muted }}>{e.params?.length ? e.params.join(",") : ""}</td>
                      <td style={{ textAlign: "right", color }}>{e.status || "—"}</td>
                      <td style={{ textAlign: "right", color: e.latency_ms > 500 ? C.amber : C.muted }}>{e.latency_ms}</td>
                      <td style={{ color: C.dim, fontSize: 9 }} title={e.caller || ""}>{(e.caller || "").split(" ← ")[0] || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 9, color: C.dim }}>polled every 3s · log: {(act.data?.log_path || "").replace("/opt/ai-stack/", "")} · tick {tick}</div>
        </div>
      )}
    </SectionCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Live Rates Ticker — auto-refresh every 2s for selected symbol+TF
// Shows the latest bar + a pulsing LIVE dot when fresh
// ══════════════════════════════════════════════════════════════════════════
const TICKER_SYMBOLS = ["XAUUSD", "EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCAD", "USDCHF", "BTCUSD", "R_100", "R_75", "R_50", "R_25", "R_10"];
const TICKER_TFS = ["M1", "M5", "M15", "H1", "H4", "D1"];

function LiveTickerCard() {
  const [symbol, setSymbol] = useState("XAUUSD");
  const [tf, setTf] = useState("M1");
  const [data, setData] = useState(null);
  const [lastUpd, setLastUpd] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/mt5-mcp/rates?symbol=${symbol}&period=${tf}&count=5`);
      const d = await r.json();
      setData(d);
      setLastUpd(Date.now());
    } catch (e) {
      setData({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  }, [symbol, tf]);

  useEffect(() => {
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [load]);

  const bars = normalizeMt5Bars(data?.data?.bars);
  const last = bars[bars.length - 1];
  const prev = bars[bars.length - 2];
  const freshness = mt5BarFreshness({ bar: last, timeframe: data?.timeframe || tf, brokerOffsetSec: data?.broker_offset_sec });
  const live = data?.ok && freshness.live;
  const tickUp = last && prev ? (last.close > prev.close ? "▲" : last.close < prev.close ? "▼" : "—") : "—";
  const tickColor = last && prev ? (last.close > prev.close ? C.green : last.close < prev.close ? C.red : C.muted) : C.muted;

  return (
    <SectionCard
      title="LIVE RATES"
      badge={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          {live && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, boxShadow: `0 0 6px ${C.green}`, animation: "mt5-pulse 1.4s infinite" }} />}
          <Badge label={freshness.label} color={live ? C.green : C.amber} />
        </span>
      }
      action={load}
      actionLabel="⟳"
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <select value={symbol} onChange={e => setSymbol(e.target.value)} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, padding: "5px 8px", fontSize: 11, fontFamily: "monospace" }}>
          {TICKER_SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={tf} onChange={e => setTf(e.target.value)} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, padding: "5px 8px", fontSize: 11, fontFamily: "monospace" }}>
          {TICKER_TFS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {loading && <span style={{ color: C.dim, fontSize: 10, alignSelf: "center" }}>⟳ loading</span>}
      </div>
      {!data?.ok ? (
        <div style={{ color: C.muted, fontSize: 11 }}>{data?.error ? `Error: ${data.error}` : "Loading…"}</div>
      ) : !last ? (
        <div style={{ color: C.muted, fontSize: 11 }}>No data.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: C.text, fontFamily: "monospace" }}>{last.close}</div>
            <div style={{ fontSize: 16, color: tickColor, fontFamily: "monospace" }}>{tickUp}</div>
            <div style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
              {freshness.timeUtc ? new Date(freshness.timeUtc * 1000).toISOString().slice(11, 19) + " UTC" : "—"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <StatCard label="OPEN" value={last.open} color={C.muted} />
            <StatCard label="HIGH" value={last.high} color={C.green} />
            <StatCard label="LOW" value={last.low} color={C.red} />
            <StatCard label="CLOSE" value={last.close} color={C.text} />
            <StatCard label="VOL" value={last.volume || last.tick_volume || "—"} color={C.cyan} />
          </div>
          {bars.length >= 2 && (
            <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
              {bars.slice(-3).map((b, i) => {
                const d = bars[bars.length - 3 + i];
                if (!d) return null;
                const ch = b.close - b.open;
                return (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "2px 0", color: ch >= 0 ? C.green : C.red }}>
                    <span style={{ minWidth: 100 }}>{b.time && Number.isFinite(data.broker_offset_sec) ? new Date((b.time - data.broker_offset_sec) * 1000).toISOString().slice(11, 19) : "—"}</span>
                    <span>O {b.open}</span>
                    <span>H {b.high}</span>
                    <span>L {b.low}</span>
                    <span>C {b.close}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EA Loop Progress Card — 4 EAs, last tick, best per-EA result, pending count
// Reads /opt/ai-stack/data/ea_loop/ea_results.db via /api/ea-loop/progress
// ══════════════════════════════════════════════════════════════════════════
function EaLoopProgressCard() {
  const [prog, setProg] = useState(null);
  const load = useCallback(() => {
    apiFetch("/api/ea-loop/progress")
      .then(r => r.json())
      .then(setProg)
      .catch(e => setProg({ ok: false, error: e.message }));
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const data = prog?.data || {};
  const eas = data.eas || [];
  const ticks = data.ticks || 0;
  const trials = data.trials || 0;
  const candidates = data.candidates || 0;
  const lastTick = data.last_tick;
  const lastTickAgo = lastTick ? Math.max(0, Math.round((Date.now() - new Date(lastTick).getTime()) / 1000)) : null;

  return (
    <SectionCard
      title={`EA LOOP — SELF-IMPROVING (${eas.length} EAs)`}
      badge={
        prog?.ok ? (
          <Badge
            label={lastTickAgo == null ? "—" : lastTickAgo < 360 ? "RECENT" : lastTickAgo < 7200 ? "RECENT" : "STALE"}
            color={lastTickAgo == null ? C.dim : lastTickAgo < 360 ? C.green : lastTickAgo < 7200 ? C.amber : C.red}
          />
        ) : <Badge label="LOADING" color={C.muted} />
      }
      action={load}
      actionLabel="⟳"
    >
      {!prog?.ok ? (
        <div style={{ color: C.muted, fontSize: 11 }}>{prog?.error ? `Error: ${prog.error}` : "Loading EA loop…"}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 10, color: C.muted }}>Latest tick: {data.latest_tick ? JSON.stringify(data.latest_tick) : "—"}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <StatCard label="LOOP TICKS" value={ticks} sub={lastTick ? `${lastTickAgo}s ago` : "—"} color={C.cyan} />
            <StatCard label="TRIALS RUN" value={trials} sub={`${eas.length} EAs`} color={C.text} />
            <StatCard label="CANDIDATES" value={candidates} sub="awaiting promotion" color={candidates > 0 ? C.amber : C.muted} />
            <StatCard label="EA PENDING" value={eas.filter(e => e.pending).length} sub={`${eas.length} total`} color={C.purple} />
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: "left" }}>
                  <th style={{ padding: "5px 8px" }}>EA</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>Best WR</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>Best P&L (USD)</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>Best PF</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>N</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>Δ vs Active</th>
                  <th style={{ padding: "5px 8px", textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {eas.map(ea => {
                  const beats = ea.delta_pnl > 0;
                  return (
                    <tr key={ea.name} style={{ borderTop: `1px solid ${C.border}33` }}>
                      <td style={{ padding: "4px 8px", fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{ea.name.replace(/^EA_\d+_/, "")}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: ea.best_wr >= 55 ? C.green : C.amber, fontFamily: "monospace" }}>
                        {ea.best_wr != null ? `${ea.best_wr.toFixed(1)}%` : "—"}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: (ea.best_pnl || 0) >= 0 ? C.green : C.red, fontFamily: "monospace", fontWeight: 700 }}>
                        {ea.best_pnl != null ? `$${ea.best_pnl.toFixed(0)}` : "—"}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: (ea.best_pf || 0) >= 1.3 ? C.green : C.amber, fontFamily: "monospace" }}>
                        {ea.best_pf != null ? ea.best_pf.toFixed(2) : "—"}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: C.muted, fontFamily: "monospace" }}>{ea.best_n || "—"}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: beats ? C.green : C.red, fontFamily: "monospace", fontWeight: 700 }}>
                        {ea.delta_pnl != null ? `${ea.delta_pnl >= 0 ? "+" : ""}$${ea.delta_pnl.toFixed(0)}` : "—"}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "center" }}>
                        {ea.pending ? <Badge label="PENDING" color={C.amber} /> : <Badge label="NO PENDING" color={C.muted} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {candidates > 0 && (
            <div style={{ fontSize: 11, color: C.amber, background: `${C.amber}11`, border: `1px solid ${C.amber}55`, borderRadius: 6, padding: "8px 10px" }}>
              ⚠ {candidates} candidate{candidates > 1 ? "s" : ""} pending file{candidates > 1 ? "s" : ""}. Awaiting review at <code>/api/ea-loop/pending</code>.
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// Parse account_info plain string into readable fields
// Format: login, group, leverage, ... fields follow
// Fields appear to be: login(0), group(1), leverage(2), server(3), currency(4), company(5)
// Then: balance, credit, profit, equity, margin, margin_free, margin_level, ... etc
function parseAccountInfo(raw) {
  // Server now returns labelled fields (login, server, balance, equity, profit, currency, ...)
  if (raw && typeof raw === "object" && !Array.isArray(raw) && raw.login != null) return raw;
  if (!raw || typeof raw !== "string") return { raw };
  // Try comma-separated first
  const parts = raw.split(",").map(p => p.trim()).filter(Boolean);
  if (parts.length >= 10) {
    return {
      login: parts[0],
      group: parts[1],
      leverage: parts[2],
      server: parts[3],
      currency: parts[4],
      company: parts[5],
      balance: parts[6],
      credit: parts[7],
      profit: parts[8],
      equity: parts[9],
    };
  }
  // Fallback: try space-separated numeric blocks
  return { raw };
}

function MT5Tab() {
  const [health, setHealth] = useState(null);
  const [healthNow, setHealthNow] = useState(Date.now);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [terminalInfo, setTerminalInfo] = useState(null);
  const [symbols, setSymbols] = useState(null);
  const [rates, setRates] = useState(null);
  const [ratesSymbol, setRatesSymbol] = useState("XAUUSD");
  const [ratesTf, setRatesTf] = useState("M5");
  const [loading, setLoading] = useState({ health: false, account: false, positions: false, terminal: false, symbols: false, rates: false });

  const fetchEndpoint = useCallback(async (endpoint, setter, key) => {
    setLoading(l => ({ ...l, [key]: true }));
    try {
      const r = await apiFetch(`/api/mt5-mcp/${endpoint}`);
      const d = await r.json();
      // Receive time lets freshness be judged on the server's clock (browser clocks drift).
      setter(d && typeof d === "object" && !Array.isArray(d) ? { ...d, _received_at: Date.now() } : d);
    } catch (e) {
      setter({ ok: false, error: e.message });
    } finally {
      setLoading(l => ({ ...l, [key]: false }));
    }
  }, []);

  const loadAll = useCallback(() => {
    fetchEndpoint("health", setHealth, "health");
    fetchEndpoint("account", setAccount, "account");
    fetchEndpoint("positions", setPositions, "positions");
    fetchEndpoint("terminal-info", setTerminalInfo, "terminal");
    fetchEndpoint("symbols", setSymbols, "symbols");
    fetchEndpoint(`rates?symbol=${ratesSymbol}&period=${ratesTf}&count=10`, setRates, "rates");
  }, [fetchEndpoint, ratesSymbol, ratesTf]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  useEffect(() => {
    const refresh = setInterval(() => fetchEndpoint("health", setHealth, "health"), 15000);
    // Expire the badge even if an in-flight health request hangs.
    const clock = setInterval(() => setHealthNow(Date.now()), 1000);
    return () => { clearInterval(refresh); clearInterval(clock); };
  }, [fetchEndpoint]);

  // The server caches health for 30 s and we poll every 15 s, so a healthy cached answer can be
  // up to ~45 s old — the old 30 s cutoff flipped the badge to DISCONNECTED every cycle.
  // Age is measured on the server's clock (server_time) plus time since receipt, so a browser
  // clock that runs behind the server no longer makes fresh results look "from the future".
  const checkedAt = health?.data?.checked_at;
  const healthServerTime = Number(health?.data?.server_time);
  const healthReceivedAt = Number(health?._received_at);
  const healthAgeMs = Number.isFinite(healthServerTime) && healthServerTime > 0 && Number.isFinite(healthReceivedAt)
    ? (healthServerTime - checkedAt) + (healthNow - healthReceivedAt)
    : healthNow - checkedAt;
  const healthFresh = Number.isFinite(checkedAt) && checkedAt > 0
    && healthAgeMs >= 0 && healthAgeMs < 60000;
  const isWorkerOk = health?.ok === true && healthFresh && health?.data?.worker_reachable === true;
  const acct = parseAccountInfo(account?.data);
  const symCount = symbols?.data?.count || (Array.isArray(symbols?.data) ? symbols.data.length : null);
  const posList = positions?.data;
  const isPositionsArray = Array.isArray(posList);
  const ratesBars = rates?.data?.bars || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "0 0 24px 0" }}>

      {/* ── Connection Banner ── */}
      <div style={{
        background: isWorkerOk ? `${C.green}15` : `${C.red}15`,
        border: `1px solid ${isWorkerOk ? C.green : C.red}55`,
        borderRadius: 8, padding: "10px 14px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 20 }}>{isWorkerOk ? "✅" : "🔴"}</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: isWorkerOk ? C.green : C.red }}>
              MT5 {isWorkerOk ? "CONNECTED" : "DISCONNECTED"}
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>
              {health?.data?.version ? `MCP v${health.data.version} · ` : ""}
              {isWorkerOk ? "Worker reachable" : !healthFresh ? "Health unverified or expired" : health?.data?.front_up ? "Front online; worker unreachable" : "Front unavailable"},
              known clients: {(health?.data?.clients_known || []).join(", ") || "none"}
            </div>
          </div>
        </div>
        <button onClick={loadAll}
          style={{ padding: "6px 14px", fontSize: 11, fontWeight: 700, background: `${C.blue}22`, color: C.blue, border: `1px solid ${C.blue}55`, borderRadius: 4, cursor: "pointer" }}>
          ⟳ Refresh All
        </button>
      </div>

      {/* ── EA trading on the demo (kara-mt5-forward) — first thing on the tab ── */}
      <ForwardTestCard />

      {/* ── Quick Stats Row ── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <StatCard
          label="ACCOUNT"
          value={acct.login || "—"}
          sub={acct.server || (account?.data ? "raw data" : "no data")}
          color={C.blue}
        />
        <StatCard
          label="EQUITY"
          value={acct.equity != null ? `$${acct.equity}` : "—"}
          sub={acct.profit != null ? `P/L: ${parseFloat(acct.profit) >= 0 ? "+" : ""}$${acct.profit}` : ""}
          color={parseFloat(acct.profit) >= 0 ? C.green : C.red}
        />
        <StatCard
          label="BALANCE"
          value={acct.balance != null ? `$${acct.balance}` : "—"}
          sub={acct.currency || "—"}
          color={C.text}
        />
        <StatCard
          label="SYMBOLS"
          value={symCount != null ? symCount.toLocaleString() : "—"}
          sub="available"
          color={C.cyan}
        />
        <StatCard
          label="POSITIONS"
          value={isPositionsArray ? posList.length : "?"}
          sub={isPositionsArray ? (posList.length === 0 ? "no open positions" : "open positions") : "—"}
          color={isPositionsArray && posList.length > 0 ? C.amber : C.green}
        />
      </div>

      {/* ── MCP Activity — live stream of /api/mt5-mcp/* calls (Kenyon ask) ── */}
      <McpActivityCard />

      {/* ── EA research lab (PC Strategy Tester) — moved here from INFO 2026-10-13: all MT5/EA info lives in this tab ── */}
      <Mt5EaLab token={localStorage.getItem("kara_auth_token")} />

      {/* ── EA Self-Improving Loop (4 EAs) ── */}
      <EaLoopProgressCard />

      {/* ── Live Rates Ticker — auto-refreshes every 2s, replaces old "LIVE RATES" ── */}
      <LiveTickerCard />

      {/* ── Live rates — manual deep dive (10 bars) ── */}
      <SectionCard
        title="LIVE RATES — MANUAL DEEP DIVE"
        badge={<Badge label={`${ratesSymbol} ${ratesTf}`} color={C.cyan} />}
        action={() => fetchEndpoint(`rates?symbol=${ratesSymbol}&period=${ratesTf}&count=10`, setRates, "rates")}
        actionLabel="⟳ 10 bars"
        actionLoading={loading.rates}
      >
        {/* Symbol / TF picker */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <select value={ratesSymbol} onChange={e => setRatesSymbol(e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: "5px 8px", borderRadius: 4, fontSize: 11 }}>
            {["XAUUSD","XAGUSD","EURUSD","GBPUSD","USDJPY","AUDUSD","USDCAD","USDCHF","BTCUSD","ETHUSD"].map(s =>
              <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={ratesTf} onChange={e => setRatesTf(e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.blue, padding: "5px 8px", borderRadius: 4, fontSize: 11 }}>
            {["M1","M5","M15","M30","H1","H4","D1"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
          </select>
          <button onClick={() => fetchEndpoint(`rates?symbol=${ratesSymbol}&period=${ratesTf}&count=10`, setRates, "rates")}
            disabled={loading.rates}
            style={{ padding: "5px 12px", fontSize: 11, fontWeight: 700, background: `${C.green}22`, color: C.green, border: `1px solid ${C.green}55`, borderRadius: 4, cursor: loading.rates ? "not-allowed" : "pointer" }}>
            Load
          </button>
        </div>

        {/* Bars table */}
        {ratesBars.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Time (UTC)", "Open", "High", "Low", "Close", "Vol"].map(h => (
                    <th key={h} style={{ padding: "5px 8px", textAlign: "right", color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ratesBars.slice().reverse().map((bar, i) => {
                  const ts = new Date(bar[0] * 1000).toISOString().replace("T", " ").slice(0, 16);
                  const [o, h, l, c, v] = bar;
                  const up = parseFloat(c) >= parseFloat(o);
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}33` }}>
                      <td style={{ padding: "4px 8px", color: C.muted, fontFamily: "monospace" }}>{ts}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: up ? C.green : C.red, fontFamily: "monospace" }}>{o.toFixed(2)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: C.green, fontFamily: "monospace" }}>{h.toFixed(2)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: C.red, fontFamily: "monospace" }}>{l.toFixed(2)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: up ? C.green : C.red, fontFamily: "monospace", fontWeight: 700 }}>{c.toFixed(2)}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right", color: C.muted, fontFamily: "monospace" }}>{v}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: C.muted, fontSize: 11, textAlign: "center", padding: "16px 0" }}>
            {loading.rates ? "Loading rates..." : "No rate data. Click Load or Refresh."}
          </div>
        )}
      </SectionCard>

      {/* ── Symbols Preview ── */}
      <SectionCard
        title="SYMBOLS"
        badge={<Badge label={`${symCount?.toLocaleString() || "?"} symbols`} color={C.cyan} />}
        action={() => fetchEndpoint("symbols", setSymbols, "symbols")}
        actionLabel="⟳"
        actionLoading={loading.symbols}
      >
        {symbols?.data?.symbols && Array.isArray(symbols.data.symbols) ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {symbols.data.symbols.slice(0, 60).map(s => (
              <span key={s} style={{
                background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3,
                padding: "2px 7px", fontSize: 10, color: C.muted, fontFamily: "monospace"
              }}>{s}</span>
            ))}
            {symbols.data.symbols.length > 60 && (
              <span style={{ color: C.muted, fontSize: 10, padding: "2px 8px" }}>
                +{symbols.data.symbols.length - 60} more
              </span>
            )}
          </div>
        ) : (
          <div style={{ color: C.muted, fontSize: 11 }}>
            {symbols?.data?.count ? `${symbols.data.count} symbols available` : "No symbols data"}
          </div>
        )}
      </SectionCard>

      {/* ── Positions ── */}
      <SectionCard
        title="POSITIONS"
        badge={isPositionsArray ? <Badge label={`${posList.length} open`} color={posList.length > 0 ? C.amber : C.green} /> : <Badge label="—" color={C.dim} />}
        action={() => fetchEndpoint("positions", setPositions, "positions")}
        actionLabel="⟳"
        actionLoading={loading.positions}
      >
        {isPositionsArray && posList.length === 0 ? (
          <div style={{ color: C.green, fontSize: 11, fontWeight: 700, textAlign: "center", padding: "12px 0" }}>
            ✅ No open positions
          </div>
        ) : isPositionsArray && posList.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Symbol", "Type", "Volume", "Price", "S/L", "T/P", "Profit", "Ticket"].map(h => (
                    <th key={h} style={{ padding: "5px 8px", textAlign: "left", color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posList.map((pos, i) => {
                  // MT5 MCP returns POSITION_TYPE as a number (0 = buy, 1 = sell); older bridges sent strings.
                  const side = pos.type === 0 || pos.type === "0" ? "buy" : pos.type === 1 || pos.type === "1" ? "sell" : String(pos.type ?? pos.side ?? "").toLowerCase();
                  return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}33` }}>
                    <td style={{ padding: "4px 8px", fontWeight: 700, color: C.text }}>{pos.symbol || "—"}</td>
                    <td style={{ padding: "4px 8px", color: side === "buy" ? C.green : C.red, fontWeight: 700 }}>{side ? side.toUpperCase() : "—"}</td>
                    <td style={{ padding: "4px 8px", color: C.text }}>{pos.volume || pos.size || "—"}</td>
                    <td style={{ padding: "4px 8px", color: C.muted, fontFamily: "monospace" }}>{pos.price_open || pos.open_price || "—"}</td>
                    <td style={{ padding: "4px 8px", color: C.red, fontFamily: "monospace" }}>{pos.sl || pos.stop_loss || "—"}</td>
                    <td style={{ padding: "4px 8px", color: C.green, fontFamily: "monospace" }}>{pos.tp || pos.take_profit || "—"}</td>
                    <td style={{ padding: "4px 8px", fontFamily: "monospace", fontWeight: 700, color: (parseFloat(pos.profit) || 0) >= 0 ? C.green : C.red }}>{pos.profit != null ? `$${parseFloat(pos.profit).toFixed(2)}` : "—"}</td>
                    <td style={{ padding: "4px 8px", color: C.dim, fontFamily: "monospace" }}>{pos.ticket || pos.id || "—"}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: C.muted, fontSize: 11 }}>
            {positions?.error ? `Error: ${positions.error}` : "No position data"}
          </div>
        )}
      </SectionCard>

      {/* ── Terminal Info ── */}
      <SectionCard
        title="TERMINAL INFO"
        badge={isWorkerOk ? <Badge label="ONLINE" color={C.green} /> : <Badge label="OFFLINE" color={C.red} />}
        action={() => fetchEndpoint("terminal-info", setTerminalInfo, "terminal")}
        actionLabel="⟳"
        actionLoading={loading.terminal}
      >
        {typeof terminalInfo?.data === "string" ? (
          <pre style={{
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
            padding: "10px 12px", fontSize: 10, color: C.muted,
            fontFamily: "monospace", overflow: "auto", maxHeight: 160, margin: 0,
            whiteSpace: "pre-wrap", wordBreak: "break-all"
          }}>
            {terminalInfo.data}
          </pre>
        ) : terminalInfo?.data && typeof terminalInfo.data === "object" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "4px 14px", fontSize: 11 }}>
            {Object.entries(terminalInfo.data.labelled === false ? { values: (terminalInfo.data.values || []).join(" · ") } : terminalInfo.data).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8, borderBottom: `1px solid ${C.border}`, padding: "2px 0", minWidth: 0 }}>
                <span style={{ color: C.muted }}>{k}</span>
                <span style={{ color: v === true ? C.green : v === false ? C.dim : C.text, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(v)}>{String(v)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: C.muted, fontSize: 11 }}>
            {terminalInfo?.error ? `Error: ${terminalInfo.error}` : "No terminal data"}
          </div>
        )}
      </SectionCard>

    </div>
  );
}

export default MT5Tab;
